/* eslint-disable jsx-a11y/no-onchange */
import DefaultButton from '@/components/common/default/DefaultButton';
import { ActivityPeriod, CurrentActivity } from '@/types/SkuPage';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import uiItems from '@/constants/ui-items/index.constants';
import { SkuPriceHistoryProps } from '@/types/props/components/skus';
import { LastActivity } from '@/types/other';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { SkuEnum } from '@/types/translations/SkuEnum';
import { CommonEnum } from '@/types/translations/CommonEnum';
import LoadingComponent from '@/components/common/LoadingComponent';
import { ActivityRow } from '@/components/sku/Common/ActivityRow';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';

export default function SkuPriceHistory({
  lastActivities,
  skuMainImageUrl,
  skuBottleSizeInMl,
  skuName,
  skuReleaseDate,
  skuVolumeInPercent,
  skuOrders,
  skuRetailPrice,
  lastActivitiesLoading,
}: SkuPriceHistoryProps) {
  const { translate } = useTypedTranslation<SkuEnum>('sku');
  const { translate: translateCommon } = useTypedTranslation<CommonEnum>('common');
  const [lastActivitiesFiltered, setLastActivitiesFiltered] = useState<LastActivity[]>([]);
  const [currentActivity, setCurrentActivity] = useState<CurrentActivity>('All');
  const [period, setPeriod] = useState<ActivityPeriod>('All');
  const [visibleItems, setVisibleItems] = useState<number>(5);

  const showMoreItems = () => {
    setVisibleItems(lastActivitiesFiltered.length);
  };

  const showLessItems = () => {
    setVisibleItems(5);
  };

  useEffect(() => {
    const lastActivitiesFilter = [...lastActivities]
      .filter(act => {
        if (currentActivity === 'All') {
          return true;
        }

        return act.type === currentActivity.toLowerCase();
      })
      .filter(act => {
        if (period === 'All') {
          return true;
        }
        const dateLimit = new Date();
        switch (period) {
          case 'Week':
            dateLimit.setDate(dateLimit.getDate() - 7);
            break;
          case 'Month':
            dateLimit.setMonth(dateLimit.getMonth() - 1);
            break;
          case '6M':
            dateLimit.setMonth(dateLimit.getMonth() - 6);
            break;
          case '1Y':
            dateLimit.setFullYear(dateLimit.getFullYear() - 1);
            break;
        }
        return new Date(act.created) > dateLimit;
      });

    setLastActivitiesFiltered(lastActivitiesFilter);
    setVisibleItems(5);
  }, [lastActivities, period, currentActivity]);

  return (
    <div className="nine:tw-container max-nine:tw-relative max-nine:tw-mt-5 nine:tw-mx-auto nine:tw-max-w-7xl nine:tw-px-3 nine:tw-py-20 nine:tw-pb-0">
      <div className="tw-mb-4 tw-w-fit max-nine:tw-hidden">
        <h3 className="tw-text-2xl tw-font-semibold tw-text-text">
          {translate(SkuEnum['last_activity.last_activities'])}
        </h3>
      </div>
      <div>
        <p className="tw-text-textblack tw-text-base tw-font-medium nine:tw-hidden">
          <strong className="tw-text-lg tw-font-semibold tw-text-text">
            {translate(SkuEnum['last_activity.last_activities'])}
          </strong>
          <br />
        </p>
      </div>
      <LoadingComponent as="div" loading={lastActivitiesLoading}>
        <div className="tw-flex tw-justify-between tw-pb-4 max-nine:tw-flex-col max-nine:tw-gap-3 nine:tw-items-center">
          <div className="tw-flex tw-items-center tw-gap-3 child:tw-rounded-full child:tw-px-12 child:tw-py-1 max-nine:tw-hidden">
            {uiItems.skuPage.skuPriceHistory.currentActivity.map(activity => (
              <DefaultButton
                key={activity.activity}
                className={clsx('tw-font-medium tw-transition tw-duration-200', {
                  ['tw-bg-[#EDEDEE] tw-text-[#4F4F4F] hover:tw-bg-[#d6d6d6] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#1C5CEB] disabled:tw-bg-[#cfcfcf] disabled:tw-text-[#9e9e9e]']:
                    currentActivity !== activity.activity,
                  ['tw-bg-main tw-text-white hover:tw-bg-[#174bb6] focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-[#1C5CEB] disabled:tw-bg-[#9bbbf2] disabled:tw-text-[#e3e3e3]']:
                    currentActivity === activity.activity,
                })}
                onClick={() => setCurrentActivity(activity.activity)}
              >
                {translate(`last_activity.${activity.tKey}` as SkuEnum)}
              </DefaultButton>
            ))}
          </div>
          <div className="tw-mb-2 tw-flex tw-items-center tw-justify-end tw-rounded-md tw-border tw-border-solid tw-border-custom-gray child:tw-basis-1/5 child:tw-p-3 child:tw-font-semibold max-nine:tw-mx-auto nine:tw-ml-auto nine:tw-w-1/4">
            {uiItems.skuPage.skuPriceHistory.period.map((periodParam, index) => (
              <DefaultButton
                key={periodParam.period}
                onClick={() => setPeriod(periodParam.period)}
                className={clsx(
                  index === 0 && 'tw-rounded-bl-md tw-rounded-tl-md',
                  index === uiItems.skuPage.skuPriceHistory.period.length - 1 &&
                    'tw-rounded-br-md tw-rounded-tr-md',
                  'hover:tw-bg-main hover:tw-text-white',
                  'active:tw-bg-blue-700',
                  'focus:tw-ring-2 focus:tw-ring-main',
                  'disabled:tw-bg-gray-300 disabled:tw-text-gray-500',
                  'tw-transition tw-duration-200 tw-ease-in-out',
                  period === periodParam.period
                    ? 'tw-bg-main tw-text-white'
                    : 'tw-bg-white tw-text-textBlack'
                )}
                disabled={false}
              >
                {translate(`last_activity.${periodParam.tKey}` as SkuEnum)}
              </DefaultButton>
            ))}
          </div>
          <div className="tw-mx-auto tw-w-10/12 nine:tw-hidden">
            <div className="tw-relative">
              <select
                value={currentActivity}
                onChange={e => setCurrentActivity(e.target.value as CurrentActivity)}
                className="tw-w-full tw-appearance-none tw-rounded-lg tw-border tw-border-solid tw-border-custom-gray tw-bg-white tw-px-6 tw-py-3 tw-pr-10 tw-font-plusJakartaSans tw-text-sm tw-font-medium tw-text-text" // Espacio para el icono
              >
                {uiItems.skuPage.skuPriceHistory.currentActivity.map(activity => (
                  <option key={activity.activity} value={activity.activity}>
                    {translate(`last_activity.${activity.tKey}` as SkuEnum)}
                  </option>
                ))}
              </select>

              {/* Chevron personalizado */}
              <div className="tw-pointer-events-none tw-absolute tw-inset-y-0 tw-right-4 tw-flex tw-items-center">
                <BiChevronDown className="tw-h-5 tw-w-5 tw-text-gray-500" />
              </div>
            </div>
          </div>
        </div>
        <div className="tw-relative tw-mt-4 tw-overflow-x-auto">
          <table className="tw-w-full tw-table-auto tw-border-collapse">
            <thead className="tw-border-b tw-border-t tw-border-[#DFE0E3]">
              <tr>
                <th
                  className="tw-text-textblack tw-px-2 tw-py-2 tw-text-center tw-text-base tw-font-semibold"
                  aria-label="Placeholder"
                ></th>
                <th className="tw-text-textblack tw-px-2 tw-py-2 tw-text-left tw-text-base tw-font-semibold max-nine:tw-text-center">
                  {translate(SkuEnum['last_activity.item'])}
                </th>
                <th className="tw-text-textblack tw-px-2 tw-py-2 tw-text-center tw-text-base tw-font-semibold max-nine:tw-hidden">
                  {translate(SkuEnum['last_activity.time'])}
                </th>
                <th className="tw-text-textblack tw-px-0 tw-py-2 tw-text-center tw-text-base tw-font-semibold max-nine:tw-hidden">
                  {translate(SkuEnum['last_activity.trend'])}
                </th>
                <th className="tw-text-textblack tw-px-0 tw-py-2 tw-text-center tw-text-base tw-font-semibold max-nine:tw-hidden">
                  {translate(SkuEnum['last_activity.price'])}
                </th>
                <th className="tw-text-textblack tw-px-2 tw-py-2 tw-text-center tw-text-base tw-font-semibold">
                  {translate(SkuEnum['last_activity.action'])}
                </th>
              </tr>
            </thead>

            <tbody>
              {lastActivitiesFiltered.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="tw-text-textblack tw-border-b tw-border-[#DFE0E3] tw-px-4 tw-py-4 tw-text-center tw-text-sm tw-font-medium"
                  >
                    {translate(SkuEnum['last_activity.no_activity_for_the_selected_filters'])}
                  </td>
                </tr>
              ) : (
                lastActivitiesFiltered
                  .slice(0, visibleItems)
                  .map((activity, index) => (
                    <ActivityRow
                      key={`${activity.id}-${index}`}
                      activity={activity}
                      skuBottleSizeInMl={skuBottleSizeInMl}
                      skuName={skuName}
                      skuMainImageUrl={skuMainImageUrl}
                      skuReleaseDate={skuReleaseDate}
                      skuOrders={skuOrders}
                      skuVolumeInPercent={skuVolumeInPercent}
                      translate={translate}
                      skuRetailPrice={skuRetailPrice}
                    />
                  ))
              )}
            </tbody>
          </table>

          {!lastActivitiesLoading &&
            lastActivitiesFiltered.length > 5 &&
            visibleItems < lastActivitiesFiltered.length && (
              <div className="tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-h-[20%] tw-w-full tw-items-center tw-justify-center tw-bg-gradient-to-t tw-from-white/100 tw-via-white/80 tw-to-transparent tw-transition-all tw-duration-300 max-nine:tw-via-white/95">
                <DefaultButton
                  className="!tw-text-[16px] tw-font-semibold tw-text-main"
                  onClick={showMoreItems}
                >
                  {translateCommon(CommonEnum['see_more'])} <BiChevronDown />
                </DefaultButton>
              </div>
            )}

          {/* "Weniger anzeigen" Button wenn mehr als 5 Elemente angezeigt werden und nicht im Ladezustand */}
          {!lastActivitiesLoading && visibleItems > 5 && (
            <div className="tw-mt-4 tw-text-center">
              <DefaultButton
                onClick={showLessItems}
                className="tw-relative tw-top-0 tw-self-start !tw-text-[16px] tw-font-semibold tw-text-main"
              >
                {translate(SkuEnum['last_activity.show_less']) ||
                  translateCommon(CommonEnum['see_less'])}{' '}
                <BiChevronUp />
              </DefaultButton>
            </div>
          )}
        </div>
      </LoadingComponent>
    </div>
  );
}
